/**
 * Permission grouping for the entity group editor.
 *
 * - Read = view_* permissions
 * - Write = add_*, change_*, delete_* permissions
 * - Linked models (order/orderitem, shipment/shipmentitem, etc.) are toggled together
 * - Legacy order/invoice CRUD codenames are hidden in favour of logical provider/customer splits
 */

export const HIDDEN_MODELS = new Set([
    "orderitem",
    "invoiceitem",
    "shipmentitem",
    "receiptitem",
    "productpicture",
    "catalogentry",
    "sellcatalogentry",
    "providercatalogentry",
    "bundlecomponent",
]);

/** Generic models replaced by logical provider/customer rows in the matrix. */
export const DISPLAY_HIDDEN_MODELS = new Set([
    "order",
    "invoice",
    "catalog",
    "homekpidefinition",
]);

export const LEGACY_HIDDEN_CODENAMES = new Set([
    "view_order",
    "add_order",
    "change_order",
    "delete_order",
    "view_invoice",
    "add_invoice",
    "change_invoice",
    "delete_invoice",
    "view_orderitem",
    "add_orderitem",
    "change_orderitem",
    "delete_orderitem",
    "view_shipmentitem",
    "add_shipmentitem",
    "change_shipmentitem",
    "delete_shipmentitem",
    "view_receiptitem",
    "add_receiptitem",
    "change_receiptitem",
    "delete_receiptitem",
    "view_invoiceitem",
    "add_invoiceitem",
    "change_invoiceitem",
    "delete_invoiceitem",
    "view_catalog",
    "add_catalog",
    "change_catalog",
    "delete_catalog",
    "view_catalogentry",
    "add_catalogentry",
    "change_catalogentry",
    "delete_catalogentry",
    "view_sellcatalogentry",
    "add_sellcatalogentry",
    "change_sellcatalogentry",
    "delete_sellcatalogentry",
    "view_providercatalogentry",
    "add_providercatalogentry",
    "change_providercatalogentry",
    "delete_providercatalogentry",
    "view_entity",
    "add_entity",
    "change_entity",
    "delete_entity",
    "view_event",
    "add_event",
    "change_event",
    "delete_event",
    "view_feature",
    "add_feature",
    "change_feature",
    "delete_feature",
    "view_tag",
    "view_establishmentproduct",
    "add_establishmentproduct",
    "change_establishmentproduct",
    "delete_establishmentproduct",
    "view_tableview",
    "add_tableview",
    "change_tableview",
    "delete_tableview",
]);

export const LOGICAL_ENTITY_PREFIXES = [
    "providercatalogentry",
    "sellcatalogentry",
    "providerorder",
    "customerorder",
    "providerinvoice",
    "customerinvoice",
    "providercatalog",
    "sellcatalog",
];

export const PERMISSION_LINKED_MODELS = {
    product: ["productpicture"],
    bundle: ["bundlecomponent"],
};

export const READ_ACTIONS = ["view"];
export const WRITE_ACTIONS = ["add", "change", "delete"];

export const MODELS_LABELS = {
    allocation: "allocations",
    billingaccount: "billingAccounts",
    bundle: "technicalSheets",
    bundlecomponent: "bundleComponents",
    bundleproducttype: "bundleProductTypes",
    productionrequest: "productionRequests",
    catalog: "catalogs",
    sellcatalog: "sellCatalogs",
    providercatalog: "providerCatalogs",
    catalogassignment: "catalogAssignments",
    catalogcategory: "catalogCategories",
    catalogentry: "catalogEntries",
    catalogagentassignment: "catalogAgentAssignments",
    commercialagent: "commercialAgents",
    contractline: "customerSubscriptions",
    customer: "customers",
    customerorder: "customerOrders",
    customerinvoice: "customerInvoices",
    deliverysite: "deliverySites",
    document: "documents",
    entity: "customers",
    entitygroup: "group",
    establishment: "establishments",
    establishmentcatalog: "establishmentCatalogs",
    establishmentuser: "establishmentUsers",
    event: "events",
    feature: "features",
    group: "groups",
    invitation: "invitation",
    movement: "movements",
    operator: "operators",
    permission: "permissions",
    product: "products",
    productpicture: "productPictures",
    productlink: "providerProductRelation",
    producttype: "productTypes",
    provider: "providers",
    providerorder: "providerOrders",
    providerinvoice: "providerInvoices",
    purchaseprice: "productPrices",
    receipt: "receipts",
    sellpricecoefficient: "sellPriceCoefficients",
    format: "formats",
    settings: "settings",
    shipment: "shipments",
    stock: "warehouses",
    stockproduct: "stockProducts",
    subscription: "subscriptions",
    tableview: "tableViews",
    homekpidefinition: "homeKpis",
    tag: "tags",
    user: "users",
    possync: "posSync",
    entitygroupestablishmentpurchaselimit: "entityGroupEstablishmentPurchaseLimits",
};

export const OVERRIDE_MODELS_LABELS = {
    operator: {
        operator: "companyInformation",
        customer: "customers",
        deliverysite: "deliverySites",
    },
    customer: {
        customer: "customers",
        deliverysite: "deliverySites",
    },
};

/** Business-domain sections for the group permission matrix (order matters). */
export const PERMISSION_SECTIONS = [
    {
        key: "purchases",
        labelKey: "sectionPurchases",
        items: [
            { app: "core", displayModel: "provider", labelKey: "providers" },
            {
                app: "catalog",
                displayModel: "providercatalog",
                labelKey: "providerCatalogs",
            },
            {
                app: "core",
                displayModel: "productlink",
                labelKey: "providerProductRelation",
            },
            {
                app: "order",
                displayModel: "providerorder",
                labelKey: "providerOrders",
            },
            {
                executionOnly: true,
                executionCodenames: ["validate_providerorder"],
                itemKey: "validate_providerorder",
                labelKey: "permissionValidateProviderorder",
            },
            {
                app: "billing",
                displayModel: "providerinvoice",
                labelKey: "providerInvoices",
            },
            {
                executionOnly: true,
                executionCodenames: ["validate_providerinvoice"],
                itemKey: "validate_providerinvoice",
                labelKey: "permissionValidateProviderinvoice",
            },
        ],
    },
    {
        key: "sales",
        labelKey: "sectionSales",
        items: [
            { app: "core", displayModel: "customer" },
            { app: "catalog", displayModel: "sellcatalog", labelKey: "sellCatalogs" },
            { app: "core", displayModel: "product" },
            { app: "catalog", displayModel: "catalogassignment" },
            { app: "core", displayModel: "commercialagent" },
            { app: "catalog", displayModel: "catalogagentassignment" },
            { app: "catalog", displayModel: "sellpricecoefficient" },
            { app: "catalog", displayModel: "format" },
            {
                app: "order",
                displayModel: "customerorder",
                labelKey: "customerOrders",
            },
            { app: "order", displayModel: "allocation" },
            { app: "billing", displayModel: "billingaccount" },
            {
                app: "billing",
                displayModel: "customerinvoice",
                labelKey: "customerInvoices",
            },
            { app: "billing", displayModel: "contractline" },
        ],
    },
    {
        key: "logistics",
        labelKey: "sectionLogistics",
        items: [
            { app: "core", displayModel: "shipment" },
            { app: "core", displayModel: "receipt" },
            { app: "core", displayModel: "movement" },
            { app: "core", displayModel: "operator" },
            { app: "stock", displayModel: "stock" },
            { app: "stock", displayModel: "stockproduct" },
        ],
    },
    {
        key: "bundlesProduction",
        labelKey: "sectionBundlesProduction",
        featureCode: "bundles",
        items: [
            { app: "catalog", displayModel: "bundle", labelKey: "technicalSheets" },
            {
                app: "catalog",
                displayModel: "bundleproducttype",
                labelKey: "bundleProductTypes",
            },
            {
                app: "catalog",
                displayModel: "productionrequest",
                labelKey: "productionRequests",
            },
            {
                executionOnly: true,
                executionCodenames: ["confirm_productionrequest"],
                itemKey: "confirm_productionrequest",
                labelKey: "permissionConfirmProductionrequest",
            },
            {
                executionOnly: true,
                executionCodenames: ["confirm_productionrequest_others"],
                itemKey: "confirm_productionrequest_others",
                labelKey: "permissionConfirmProductionrequestOthers",
            },
        ],
    },
    {
        key: "establishments",
        labelKey: "sectionEstablishments",
        items: [
            { app: "core", displayModel: "establishment" },
            { app: "core", displayModel: "establishmentuser" },
            { app: "catalog", displayModel: "establishmentcatalog" },
            { app: "connectors", displayModel: "possync" },
            {
                app: "core",
                displayModel: "entitygroupestablishmentpurchaselimit",
            },
        ],
    },
    {
        key: "administration",
        labelKey: "sectionAdministration",
        items: [
            { app: "core", displayModel: "user" },
            { app: "core", displayModel: "entitygroup" },
            { app: "core", displayModel: "invitation" },
            { app: "core", displayModel: "deliverysite" },
            { app: "core", displayModel: "producttype" },
            { app: "core", displayModel: "settings" },
            { app: "core", displayModel: "document" },
            { app: "core", displayModel: "tag" },
        ],
    },
];

/** Non-CRUD permissions: one dedicated execution-only row per entry. */
export const EXECUTION_PERMISSION_PLACEMENT = [
    {
        sectionKey: "logistics",
        codenames: ["can_create_order_shipments"],
        labelKey: "permissionCanCreateOrderShipments",
    },
    {
        sectionKey: "sales",
        codenames: ["assign_customerorder"],
        labelKey: "permissionAssignCustomerorder",
    },
    {
        sectionKey: "administration",
        codenames: ["configure_entity"],
        labelKey: "permissionConfigureEntity",
    },
    {
        sectionKey: "administration",
        codenames: ["can_impersonate"],
        labelKey: "permissionCanImpersonate",
    },
    {
        sectionKey: "administration",
        codenames: ["can_create_api_key"],
        labelKey: "permissionCanCreateApiKey",
    },
    {
        sectionKey: "administration",
        codenames: ["manage_homekpi"],
        labelKey: "permissionManageHomekpi",
    },
    {
        sectionKey: "establishments",
        codenames: ["run_pos_sync"],
        labelKey: "permissionRunPosSync",
    },
    {
        sectionKey: "establishments",
        codenames: ["run_pos_sync_production"],
        labelKey: "permissionRunPosSyncProduction",
    },
    {
        sectionKey: "establishments",
        codenames: ["close_pos_daily_sale"],
        labelKey: "permissionClosePosDailySale",
    },
];

function dedupePermissionsById(permissions) {
    const seen = new Set();
    return permissions.filter((permission) => {
        if (seen.has(permission.id)) {
            return false;
        }
        seen.add(permission.id);
        return true;
    });
}

export function isCrudPermission(permission) {
    return (
        permission.codename.startsWith("view_") ||
        permission.codename.startsWith("add_") ||
        permission.codename.startsWith("change_") ||
        permission.codename.startsWith("delete_")
    );
}

export function isExecutionPermission(permission) {
    if (shouldHidePermission(permission)) {
        return false;
    }
    return !isCrudPermission(permission);
}

export function collectExecutionPermissions(allPermissions, codenames = []) {
    if (!codenames.length) {
        return [];
    }
    const codenameSet = new Set(codenames);
    return allPermissions.filter((permission) =>
        codenameSet.has(permission.codename)
    );
}

export function collectExecutionPermissionsForItem(allPermissions, item) {
    return collectExecutionPermissions(allPermissions, item.executionCodenames || []);
}

export function executionExists(allPermissions, item) {
    return collectExecutionPermissionsForItem(allPermissions, item).length > 0;
}

export function isExecutionActive(groupPermissionIds, allPermissions, item) {
    const permissions = collectExecutionPermissionsForItem(allPermissions, item);
    if (!permissions.length) {
        return false;
    }
    return permissions.every(
        (permission) => groupPermissionIds.indexOf(permission.id) !== -1
    );
}

export function getSectionItemRowKey(item) {
    if (item.itemKey) {
        return item.itemKey;
    }
    return sectionItemKey(item.app, item.displayModel);
}

function getAssignedExecutionCodenames() {
    const codenames = new Set();
    EXECUTION_PERMISSION_PLACEMENT.forEach((placement) => {
        placement.codenames.forEach((codename) => codenames.add(codename));
    });
    PERMISSION_SECTIONS.forEach((section) => {
        getInlineExecutionCodenames(section).forEach((codename) =>
            codenames.add(codename)
        );
    });
    return codenames;
}

function getInlineExecutionCodenames(section) {
    const codenames = new Set();
    section.items.forEach((item) => {
        if (!item.executionOnly || !item.executionCodenames?.length) {
            return;
        }
        item.executionCodenames.forEach((codename) => codenames.add(codename));
    });
    return codenames;
}

function enrichSectionItems(section, allPermissions) {
    const items = section.items.map((item) => ({ ...item }));
    const inlineExecutionCodenames = getInlineExecutionCodenames(section);

    EXECUTION_PERMISSION_PLACEMENT.filter(
        (placement) => placement.sectionKey === section.key
    ).forEach((placement) => {
        if (
            placement.codenames.every((codename) =>
                inlineExecutionCodenames.has(codename)
            )
        ) {
            return;
        }
        items.push({
            executionOnly: true,
            executionCodenames: [...placement.codenames],
            itemKey: placement.codenames.join("|"),
            labelKey: placement.labelKey,
        });
    });

    return items.filter((item) => isSectionItemAvailable(allPermissions, item));
}

function getOrphanExecutionItems(allPermissions) {
    const assigned = getAssignedExecutionCodenames();
    const items = [];
    const seen = new Set();

    allPermissions.forEach((permission) => {
        if (!isExecutionPermission(permission)) {
            return;
        }
        if (assigned.has(permission.codename) || seen.has(permission.codename)) {
            return;
        }
        seen.add(permission.codename);
        items.push({
            executionOnly: true,
            executionCodenames: [permission.codename],
            itemKey: permission.codename,
            labelKey: null,
        });
    });

    return items;
}

export function sectionItemKey(app, displayModel) {
    return `${app}:${displayModel}`;
}

export function isSectionItemAvailable(allPermissions, itemOrApp, displayModel) {
    const item =
        typeof itemOrApp === "object"
            ? itemOrApp
            : { app: itemOrApp, displayModel };

    if (item.executionOnly) {
        return executionExists(allPermissions, item);
    }

    return (
        levelExists(allPermissions, item.app, item.displayModel, "read") ||
        levelExists(allPermissions, item.app, item.displayModel, "write")
    );
}

export function getMappedSectionItemKeys() {
    const keys = new Set();
    PERMISSION_SECTIONS.forEach((section) => {
        section.items.forEach((item) => {
            keys.add(sectionItemKey(item.app, item.displayModel));
        });
    });
    return keys;
}

export function getOrphanSectionItems(allPermissions) {
    const mapped = getMappedSectionItemKeys();
    const orphans = [];
    const seen = new Set();

    allPermissions.forEach((permission) => {
        if (shouldHidePermission(permission)) {
            return;
        }
        const isCrud =
            permission.codename.startsWith("view_") ||
            permission.codename.startsWith("add_") ||
            permission.codename.startsWith("change_") ||
            permission.codename.startsWith("delete_");
        if (!isCrud) {
            return;
        }
        const displayModel = getDisplayModel(permission);
        if (!displayModel) {
            return;
        }
        const key = sectionItemKey(permission.app, displayModel);
        if (mapped.has(key) || seen.has(key)) {
            return;
        }
        if (!isSectionItemAvailable(allPermissions, { app: permission.app, displayModel })) {
            return;
        }
        seen.add(key);
        orphans.push({ app: permission.app, displayModel });
    });

    return orphans;
}

export function getVisiblePermissionSections(allPermissions, options = {}) {
    const enabledFeatureCodes = new Set(options.enabledFeatureCodes || []);

    const sections = PERMISSION_SECTIONS.filter((section) => {
        if (!section.featureCode) {
            return true;
        }
        return enabledFeatureCodes.has(section.featureCode);
    })
        .map((section) => ({
            key: section.key,
            labelKey: section.labelKey,
            items: enrichSectionItems(section, allPermissions),
        }))
        .filter((section) => section.items.length > 0);

    const orphanCrud = getOrphanSectionItems(allPermissions);
    const orphanExecution = getOrphanExecutionItems(allPermissions);
    const orphanItems = [...orphanCrud, ...orphanExecution];

    if (orphanItems.length > 0) {
        sections.push({
            key: "other",
            labelKey: "sectionOther",
            items: orphanItems.filter((item) =>
                isSectionItemAvailable(allPermissions, item)
            ),
        });
    }

    return sections.filter((section) => section.items.length > 0);
}

export function sortSectionItems(items, resolveLabel) {
    return [...items].sort((a, b) => {
        if (Boolean(a.executionOnly) !== Boolean(b.executionOnly)) {
            return a.executionOnly ? 1 : -1;
        }
        return resolveLabel(a).localeCompare(resolveLabel(b));
    });
}

export function collectAllItemPermissions(allPermissions, item) {
    const collected = [];

    if (!item.executionOnly && item.displayModel) {
        collectAllModelPermissions(
            allPermissions,
            item.app,
            item.displayModel
        ).forEach((permission) => collected.push(permission));
    }

    collectExecutionPermissionsForItem(allPermissions, item).forEach((permission) =>
        collected.push(permission)
    );

    return dedupePermissionsById(collected);
}

export function collectAllSectionLevelPermissions(allPermissions, section, level) {
    const collected = [];
    section.items.forEach((item) => {
        if (level === "execute") {
            collectExecutionPermissionsForItem(allPermissions, item).forEach(
                (permission) => collected.push(permission)
            );
            return;
        }
        if (item.executionOnly) {
            return;
        }
        collectPermissionsForLevel(
            allPermissions,
            item.app,
            item.displayModel,
            level
        ).forEach((permission) => collected.push(permission));
    });
    return dedupePermissionsById(collected);
}

export function getLogicalEntityFromCodename(codename) {
    for (const prefix of LOGICAL_ENTITY_PREFIXES) {
        if (codename.endsWith(`_${prefix}`)) {
            return prefix;
        }
    }
    return null;
}

export function shouldHidePermission(permission) {
    if (HIDDEN_MODELS.has(permission.model)) {
        return true;
    }
    if (LEGACY_HIDDEN_CODENAMES.has(permission.codename)) {
        return true;
    }
    return false;
}

export function getDisplayModel(permission) {
    if (shouldHidePermission(permission)) {
        return null;
    }
    const logicalEntity = getLogicalEntityFromCodename(permission.codename);
    if (logicalEntity) {
        return logicalEntity;
    }
    if (DISPLAY_HIDDEN_MODELS.has(permission.model)) {
        return null;
    }
    return permission.model;
}

export function findPermission(allPermissions, action, app, displayModel) {
    const codename = `${action}_${displayModel}`;
    return allPermissions.find(
        (permission) =>
            permission.app === app && permission.codename === codename
    );
}

export function getLinkedDisplayModels(displayModel) {
    return PERMISSION_LINKED_MODELS[displayModel] || [];
}

export function getActionsForLevel(level) {
    return level === "read" ? READ_ACTIONS : WRITE_ACTIONS;
}

export function collectRowPermissionsForLevel(
    allPermissions,
    app,
    displayModel,
    level
) {
    const actions = getActionsForLevel(level);
    const collected = [];

    actions.forEach((action) => {
        const permission = findPermission(allPermissions, action, app, displayModel);
        if (permission) {
            collected.push(permission);
        }
    });

    return collected;
}

export function collectPermissionsForLevel(allPermissions, app, displayModel, level) {
    const actions = getActionsForLevel(level);
    const models = [displayModel, ...getLinkedDisplayModels(displayModel)];
    const collected = [];

    models.forEach((model) => {
        actions.forEach((action) => {
            const permission = findPermission(allPermissions, action, app, model);
            if (permission) {
                collected.push(permission);
            }
        });
    });

    return collected;
}

export function isLevelActive(groupPermissionIds, allPermissions, app, displayModel, level) {
    const permissions = collectRowPermissionsForLevel(
        allPermissions,
        app,
        displayModel,
        level
    );
    if (!permissions.length) {
        return false;
    }
    return permissions.every(
        (permission) => groupPermissionIds.indexOf(permission.id) !== -1
    );
}

export function levelExists(allPermissions, app, displayModel, level) {
    return (
        collectRowPermissionsForLevel(allPermissions, app, displayModel, level).length >
        0
    );
}

export function collectAllModelPermissions(allPermissions, app, displayModel) {
    return [
        ...collectPermissionsForLevel(allPermissions, app, displayModel, "read"),
        ...collectPermissionsForLevel(allPermissions, app, displayModel, "write"),
    ];
}

export function formatPermissionCodename(permission) {
    return `${permission.app}.${permission.codename}`;
}

export function formatPermissionDebugLabel(permission) {
    return `${formatPermissionCodename(permission)} (#${permission.id})`;
}

export function getItemPermissionDebug(allPermissions, itemOrApp, displayModel) {
    const item =
        typeof itemOrApp === "object"
            ? itemOrApp
            : { app: itemOrApp, displayModel };

    if (item.executionOnly) {
        const execution = collectExecutionPermissionsForItem(allPermissions, item);
        return {
            read: [],
            write: [],
            execution: execution.map(formatPermissionDebugLabel),
            all: execution.map(formatPermissionDebugLabel),
            linkedModels: [],
        };
    }

    const read = collectPermissionsForLevel(
        allPermissions,
        item.app,
        item.displayModel,
        "read"
    );
    const write = collectPermissionsForLevel(
        allPermissions,
        item.app,
        item.displayModel,
        "write"
    );
    const execution = collectExecutionPermissionsForItem(allPermissions, item);
    const all = collectAllItemPermissions(allPermissions, item);
    const linkedModels = getLinkedDisplayModels(item.displayModel);

    return {
        read: read.map(formatPermissionDebugLabel),
        write: write.map(formatPermissionDebugLabel),
        execution: execution.map(formatPermissionDebugLabel),
        all: all.map(formatPermissionDebugLabel),
        linkedModels,
    };
}

export const SELL_CATALOG_PERMISSIONS = {
    view: "catalog.view_sellcatalog",
    add: "catalog.add_sellcatalog",
    change: "catalog.change_sellcatalog",
    delete: "catalog.delete_sellcatalog",
};

export const PROVIDER_CATALOG_PERMISSIONS = {
    view: "catalog.view_providercatalog",
    add: "catalog.add_providercatalog",
    change: "catalog.change_providercatalog",
    delete: "catalog.delete_providercatalog",
};

export const SELL_CATALOG_ENTRY_PERMISSIONS = SELL_CATALOG_PERMISSIONS;

export const PROVIDER_CATALOG_ENTRY_PERMISSIONS = PROVIDER_CATALOG_PERMISSIONS;

export const PROVIDER_ORDER_PERMISSIONS = {
    view: "order.view_providerorder",
    add: "order.add_providerorder",
    change: "order.change_providerorder",
    delete: "order.delete_providerorder",
};

export const CUSTOMER_ORDER_PERMISSIONS = {
    view: "order.view_customerorder",
    add: "order.add_customerorder",
    change: "order.change_customerorder",
    delete: "order.delete_customerorder",
};

export const PROVIDER_INVOICE_PERMISSIONS = {
    view: "billing.view_providerinvoice",
    add: "billing.add_providerinvoice",
    change: "billing.change_providerinvoice",
    delete: "billing.delete_providerinvoice",
    validate: "billing.validate_providerinvoice",
    paid: "billing.paid_providerinvoice",
};

export const CUSTOMER_INVOICE_PERMISSIONS = {
    view: "billing.view_customerinvoice",
    add: "billing.add_customerinvoice",
    change: "billing.change_customerinvoice",
    delete: "billing.delete_customerinvoice",
    validate: "billing.validate_customerinvoice",
    credit: "billing.credit_customerinvoice",
    send: "billing.send_customerinvoice",
    paid: "billing.paid_customerinvoice",
    replay: "billing.replay_customerinvoiceitem",
};

export const LEGACY_PERMISSION_ALIASES = {
    "order.view_order": [
        PROVIDER_ORDER_PERMISSIONS.view,
        CUSTOMER_ORDER_PERMISSIONS.view,
        "order.view_order",
    ],
    "order.add_order": [
        PROVIDER_ORDER_PERMISSIONS.add,
        CUSTOMER_ORDER_PERMISSIONS.add,
        "order.add_order",
    ],
    "order.change_order": [
        PROVIDER_ORDER_PERMISSIONS.change,
        CUSTOMER_ORDER_PERMISSIONS.change,
        "order.change_order",
    ],
    "order.delete_order": [
        PROVIDER_ORDER_PERMISSIONS.delete,
        CUSTOMER_ORDER_PERMISSIONS.delete,
        "order.delete_order",
    ],
    "order.view_orderitem": [
        PROVIDER_ORDER_PERMISSIONS.view,
        CUSTOMER_ORDER_PERMISSIONS.view,
        "order.view_orderitem",
    ],
    "order.add_orderitem": [
        PROVIDER_ORDER_PERMISSIONS.add,
        CUSTOMER_ORDER_PERMISSIONS.add,
        "order.add_orderitem",
    ],
    "order.change_orderitem": [
        PROVIDER_ORDER_PERMISSIONS.change,
        CUSTOMER_ORDER_PERMISSIONS.change,
        "order.change_orderitem",
    ],
    "order.delete_orderitem": [
        PROVIDER_ORDER_PERMISSIONS.delete,
        CUSTOMER_ORDER_PERMISSIONS.delete,
        "order.delete_orderitem",
    ],
    "billing.view_invoice": [
        PROVIDER_INVOICE_PERMISSIONS.view,
        CUSTOMER_INVOICE_PERMISSIONS.view,
        "billing.view_invoice",
    ],
    "billing.add_invoice": [
        PROVIDER_INVOICE_PERMISSIONS.add,
        CUSTOMER_INVOICE_PERMISSIONS.add,
        "billing.add_invoice",
    ],
    "billing.change_invoice": [
        PROVIDER_INVOICE_PERMISSIONS.change,
        CUSTOMER_INVOICE_PERMISSIONS.change,
        "billing.change_invoice",
    ],
    "billing.delete_invoice": [
        PROVIDER_INVOICE_PERMISSIONS.delete,
        CUSTOMER_INVOICE_PERMISSIONS.delete,
        "billing.delete_invoice",
    ],
    "catalog.view_catalog": [
        SELL_CATALOG_PERMISSIONS.view,
        PROVIDER_CATALOG_PERMISSIONS.view,
        "catalog.view_catalog",
    ],
    "catalog.add_catalog": [
        SELL_CATALOG_PERMISSIONS.add,
        PROVIDER_CATALOG_PERMISSIONS.add,
        "catalog.add_catalog",
    ],
    "catalog.change_catalog": [
        SELL_CATALOG_PERMISSIONS.change,
        PROVIDER_CATALOG_PERMISSIONS.change,
        "catalog.change_catalog",
    ],
    "catalog.delete_catalog": [
        SELL_CATALOG_PERMISSIONS.delete,
        PROVIDER_CATALOG_PERMISSIONS.delete,
        "catalog.delete_catalog",
    ],
    "catalog.view_catalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.view,
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.view,
        SELL_CATALOG_PERMISSIONS.view,
        PROVIDER_CATALOG_PERMISSIONS.view,
        "catalog.view_catalog",
    ],
    "catalog.add_catalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.add,
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.add,
        SELL_CATALOG_PERMISSIONS.add,
        PROVIDER_CATALOG_PERMISSIONS.add,
        "catalog.add_catalog",
    ],
    "catalog.change_catalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.change,
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.change,
        SELL_CATALOG_PERMISSIONS.change,
        PROVIDER_CATALOG_PERMISSIONS.change,
        "catalog.change_catalog",
    ],
    "catalog.delete_catalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.delete,
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.delete,
        SELL_CATALOG_PERMISSIONS.delete,
        PROVIDER_CATALOG_PERMISSIONS.delete,
        "catalog.delete_catalog",
    ],
    "catalog.view_sellcatalog": [
        SELL_CATALOG_PERMISSIONS.view,
        "catalog.view_catalog",
    ],
    "catalog.add_sellcatalog": [
        SELL_CATALOG_PERMISSIONS.add,
        "catalog.add_catalog",
    ],
    "catalog.change_sellcatalog": [
        SELL_CATALOG_PERMISSIONS.change,
        "catalog.change_catalog",
    ],
    "catalog.delete_sellcatalog": [
        SELL_CATALOG_PERMISSIONS.delete,
        "catalog.delete_catalog",
    ],
    "catalog.view_providercatalog": [
        PROVIDER_CATALOG_PERMISSIONS.view,
        "catalog.view_catalog",
    ],
    "catalog.add_providercatalog": [
        PROVIDER_CATALOG_PERMISSIONS.add,
        "catalog.add_catalog",
    ],
    "catalog.change_providercatalog": [
        PROVIDER_CATALOG_PERMISSIONS.change,
        "catalog.change_catalog",
    ],
    "catalog.delete_providercatalog": [
        PROVIDER_CATALOG_PERMISSIONS.delete,
        "catalog.delete_catalog",
    ],
    "catalog.view_sellcatalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.view,
        SELL_CATALOG_PERMISSIONS.view,
        "catalog.view_catalogentry",
        "catalog.view_catalog",
    ],
    "catalog.add_sellcatalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.add,
        SELL_CATALOG_PERMISSIONS.add,
        "catalog.add_catalogentry",
        "catalog.add_catalog",
    ],
    "catalog.change_sellcatalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.change,
        SELL_CATALOG_PERMISSIONS.change,
        "catalog.change_catalogentry",
        "catalog.change_catalog",
    ],
    "catalog.delete_sellcatalogentry": [
        SELL_CATALOG_ENTRY_PERMISSIONS.delete,
        SELL_CATALOG_PERMISSIONS.delete,
        "catalog.delete_catalogentry",
        "catalog.delete_catalog",
    ],
    "catalog.view_providercatalogentry": [
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.view,
        PROVIDER_CATALOG_PERMISSIONS.view,
        "catalog.view_catalogentry",
        "catalog.view_catalog",
    ],
    "catalog.add_providercatalogentry": [
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.add,
        PROVIDER_CATALOG_PERMISSIONS.add,
        "catalog.add_catalogentry",
        "catalog.add_catalog",
    ],
    "catalog.change_providercatalogentry": [
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.change,
        PROVIDER_CATALOG_PERMISSIONS.change,
        "catalog.change_catalogentry",
        "catalog.change_catalog",
    ],
    "catalog.delete_providercatalogentry": [
        PROVIDER_CATALOG_ENTRY_PERMISSIONS.delete,
        PROVIDER_CATALOG_PERMISSIONS.delete,
        "catalog.delete_catalogentry",
        "catalog.delete_catalog",
    ],
    "core.view_shipmentitem": ["core.view_shipment", "core.view_shipmentitem"],
    "core.add_shipmentitem": ["core.add_shipment", "core.add_shipmentitem"],
    "core.change_shipmentitem": ["core.change_shipment", "core.change_shipmentitem"],
    "core.delete_shipmentitem": ["core.delete_shipment", "core.delete_shipmentitem"],
    "core.view_receiptitem": ["core.view_receipt", "core.view_receiptitem"],
    "core.add_receiptitem": ["core.add_receipt", "core.add_receiptitem"],
    "core.change_receiptitem": ["core.change_receipt", "core.change_receiptitem"],
    "core.delete_receiptitem": ["core.delete_receipt", "core.delete_receiptitem"],
};

export const CATALOG_ASSIGNMENT_WRITE_PERMISSIONS = [
    "catalog.add_catalogassignment",
    "catalog.change_catalogassignment",
    "catalog.delete_catalogassignment",
];

export function userCanManageCatalogAssignments(userPermissions) {
    return userHasAnyPermission(
        userPermissions,
        CATALOG_ASSIGNMENT_WRITE_PERMISSIONS
    );
}

export function userHasAnyPermission(userPermissions, candidates) {
    return candidates.some(
        (candidate) => userPermissions.indexOf(candidate) !== -1
    );
}

export function resolvePermissionCheck(userPermissions, perm) {
    if (userPermissions.indexOf(perm) !== -1) {
        return true;
    }
    const aliases = LEGACY_PERMISSION_ALIASES[perm];
    if (aliases) {
        return userHasAnyPermission(userPermissions, aliases);
    }
    return false;
}
